import { Component, h, primitiveNodeTypes, useMemo, useRef, useState } from "@lukekaalim/act"
import { html, SVG, svg } from "@lukekaalim/act-web"
import { TheatreCutout, TheatreState } from "../../hooks/useTheatreState";

import classes from './ContentBrowser.module.css';
import commonClasses from './ContentBrowser/index.module.css';

import { useElementRect } from "@kosmod/graphics-2d/hooks";
import { ID, useAsync, useLogger } from "@kosmod/kit";
import { inputBeat, OrbitCamera, renderBeat, Snapshot } from "@kosmod/graphics-3d";
import { a3, ThreeJSRoot } from "@lukekaalim/act-three";
import { BoxGeometry, Color, Euler, MeshBasicMaterial, PerspectiveCamera, Scene, TextureLoader } from "three";
import { AssetID, AssetMeta } from "@kosmod/assets";
import { MiniTemplateID, TheatrePosition } from "../../defs";
import { MiniTemplateEditor } from "./ContentBrowser/MiniEditor";
import { ToolList } from "./ToolBelt/ToolList";
import { AnyLogicState } from "../../defs/logic/all";
import { DiceList } from "./ContentBrowser/DiceList";
import { QuickSpriteGraphicRenderer } from "../Theatre/Mini/QuickSprite";
import { useTheatreContentManager } from "../../hooks/useTheatreContentManager";
import { MiniTemplateList } from "./ContentBrowser/MiniList";
import { ToolID } from "../../defs/tools/define";
import { assetsURLs } from "../../assets";

export type ContentBrowserProps = {
  state: TheatreState,
}

export type ContentBrowserTarget =
  | { type: 'none' }
  | { type: 'edit-mini-template', miniTemplateId: MiniTemplateID }
  | { type: 'create-mini-template'}

export const ContentBrowser: Component<ContentBrowserProps> = ({ state }) => {
  const logger = useLogger('ContentBrowser');

  const ref = useRef<HTMLElement | null>(null);


  let [target, setTarget] = useState<ContentBrowserTarget>({ type: 'none' });

  if (state.viewMode !== 'content-browser')
    target = { type: 'none' }

  const miniTemplates = state.data.miniTemplates;

  const targetMiniTemplate = target.type === 'edit-mini-template' && miniTemplates.get(target.miniTemplateId);

  const {content} = state;

  return [
    h(html.div, { className: classes.contentBrowserContainer, ref }, [
      h(html.div, { className: classes.titleHeader }, [
        h(html.h1, {}, 'Content Browser'),
        h(html.button, { onClick(event) {
          state.controller.refresh();
        }, }, 'Reload'),
      ]),
      h(html.p, {}, `Click and drag icons to attach them to your toolbar, to give you new ways of controlling the scene.`),
      h(html.p, {}, `The Refresh button lets you synchronize other players uploaded content.`),
      h('hr', { className: classes.line }),
      h(html.div, { className: classes.titleHeader }, [
        h(html.h2, {}, 'Toolbelts'),
        h(html.button, { className: classes.insertNewItemButton, onClick() {
          state.tools.addToolbelt();
        } }, '+'),
      ]),
      h(html.p, {}, `A toolbelt is the collection of tools at the bottom of the screen. You can edit or switch toolbelts here`),
      h(html.section, { className: classes.contentSection },
        h(ToolList, { tools: [], state }),
      ),
      h('hr', { className: classes.line }),
      h(html.h2, {}, 'Tools'),
      h(html.section, { className: classes.contentSection },
        h(html.ol, { className: commonClasses.squareRowList }, [
          h(html.li, { className: commonClasses.squareRowListItem, draggable: true, onDragStart(event) {
            event.dataTransfer?.setDragImage(state.selection.emptyDragImage, 0, 0);

            const toolId: ToolID = ID('theatre:tool');
            state.selection.startDrag({ type: 'tool', toolId, toolState: {
              id: toolId, type: 'selection:select', filter: 'mini', operation: 'additive'
            } })
          }, },
            h(html.img, { src: assetsURLs.icons.select })
          ),
          h(html.li, { className: commonClasses.squareRowListItem, draggable: true, onDragStart(event) {
            event.dataTransfer?.setDragImage(state.selection.emptyDragImage, 0, 0);

            const toolId: ToolID = ID('theatre:tool');
            state.selection.startDrag({ type: 'tool', toolId, toolState: {
              id: toolId, type: 'dice:cleanup', radius: 5,
            } })
          }, },
            h(html.img, { src: assetsURLs.icons.cleanup }),
          ),
          h(html.li, { className: commonClasses.squareRowListItem, draggable: true, onDragStart(event) {
            event.dataTransfer?.setDragImage(state.selection.emptyDragImage, 0, 0);

            const toolId: ToolID = ID('theatre:tool');
            state.selection.startDrag({ type: 'tool', toolId, toolState: {
              id: toolId, type: 'dev:vis', canvas: 'none'
            } })
          }, },
            h(html.img, { src: assetsURLs.icons.bug }),
          ),
        ]),
      ),
      h('hr', { className: classes.line }),
      h(html.h2, {}, 'Dice'),
      h(html.section, { className: classes.contentSection },
        h(DiceList, { state }),
      ),
      h('hr', { className: classes.line }),
      h(html.div, { className: classes.titleHeader }, [
        h(html.h2, {}, 'Minis'),
        h(html.button, { className: classes.insertNewItemButton, onClick() {
          setTarget({ type: 'create-mini-template' });
        } }, '+'),
      ]),
      h(html.p, {}, `Minis are interactive objects that can be placed in the world on a surface`),
      h(html.section, { className: classes.contentSection },
        h(MiniTemplateList, {
          miniTemplates: [...miniTemplates.values()],
          resources: state.sceneRes,
          selected: targetMiniTemplate && targetMiniTemplate.id || null,

          graphicsSetup: state.graphics,
          content,

          onClick(event, miniTemplate) {
            // (event.target as HTMLElement).scrollIntoView({ block: 'start', behavior: 'smooth' })
            setTarget({ type: 'edit-mini-template', miniTemplateId: miniTemplate.id })
          }, 
          onDragStart(event, miniTemplate) {
            const data = event.dataTransfer;
            if (!data)
              return;

            data.setData('kosmod:mini-template-id', miniTemplate.id)
            
            data.setDragImage(state.selection.emptyDragImage, 0, 0);
            const toolId: ToolID = ID('theatre:tool');
            state.selection.startDrag({ type: 'tool', toolId, toolState: {
              type: 'minis:place',
              placementCounter: 0,
              id: toolId,
              source: { type: 'template', miniTemplateId: miniTemplate.id }
            } })
          },
        })
      ),
      !!targetMiniTemplate && h(MiniTemplateEditor, {
        key: targetMiniTemplate.id,
        state,
        miniTemplate: targetMiniTemplate,
        content,
        graphics: state.graphics,
        resources: state.sceneRes,
        async onMiniTemplateChange({ name, logic }) {
          await content.updateMiniTemplate({ ...targetMiniTemplate, name, logic })
          logger.log('Calling Refresh')
          state.controller.refresh();
        },
      }),
      target.type === 'create-mini-template' && h(MiniTemplateEditor, {
        key: target.type,
        state,
        miniTemplate: { name: 'New Mini', logic: [] },
        content,
        graphics: state.graphics,
        resources: state.sceneRes,
        async onMiniTemplateChange({ name, logic }) {
          const miniTemplate = await content.createNewMiniTemplate(name, logic)
          state.controller.refresh();
          setTarget({ type: 'edit-mini-template', miniTemplateId: miniTemplate.id });
        },
      }),
      h('hr', { className: classes.line }),
      h(html.h2, {}, 'Terrain Brush'),
      h(html.p, {}, `A terrain brush creates floors or walls on a surface`),
      h(html.section, { className: classes.contentSection },
        h(html.ol, { className: commonClasses.squareRowList },[
            h(html.li, { className: commonClasses.squareRowListItem, draggable: true, onDragStart(event) {
              event.dataTransfer?.setDragImage(state.selection.emptyDragImage, 0, 0);

              const toolId: ToolID = ID('theatre:tool');
              state.selection.startDrag({ type: 'tool', toolId, toolState: {
                id: toolId, type: 'terrain:paint', brush: null, part: 'floor', start: null
              } })
            }, },
              'Erase'
            ),
          [...content.brushes.values()].map(brush => {
            const dioramaModel = state.sceneRes.brushParts.get(brush.id)?.diorama || null;

            return [
              h(html.li, { className: commonClasses.squareRowListItem, draggable: true, onDragStart(event) {
                event.dataTransfer?.setDragImage(state.selection.emptyDragImage, 0, 0);

                const toolId: ToolID = ID('theatre:tool');
                state.selection.startDrag({ type: 'tool', toolId, toolState: {
                  id: toolId, type: 'terrain:paint', brush: brush.id, part: 'floor', start: null
                } })
              }, },
                [
                  h(Snapshot, { object: dioramaModel, graphics: state.graphics }),
                  h(html.div, { style: { position: 'absolute', color: 'black', bottom: '0px' } }, brush.name)
                ]
              ),
            ]
          })
        ]),
      ),
    ])
  ];
}


export * from './ContentBrowser/index';
